// Motor de búsqueda del Asistente virtual.
//
// Recibe una query en lenguaje natural y devuelve:
//   { hit, suggestions }
//     hit         → mejor entrada de la KB (o null si nada supera el umbral)
//     suggestions → hasta 3 entradas relacionadas
//
// Algoritmo: tokenización + normalización (sin acentos, minúsculas,
// quitar palabras vacías) + scoring por coincidencia de tokens
// contra keywords/question/answer con pesos distintos. TF-like
// simple, sin embeddings ni LLM — funciona 100% offline.
//
// Este archivo se puede reemplazar por un cliente HTTP al motor
// RAG en el futuro sin tocar el componente AssistantTab.

import KB from '../data/assistantKB';

// Palabras vacías comunes en español (una parte pequeña, no todas).
const STOPWORDS = new Set([
  'a','al','ante','bajo','con','contra','de','del','desde','durante',
  'en','entre','hacia','hasta','mediante','para','por','segun','sin',
  'so','sobre','tras','y','o','u','el','la','los','las','un','una',
  'unos','unas','lo','le','les','me','te','se','nos','os','mi','tu',
  'su','sus','mis','tus','esto','esta','este','ese','esa','eso','que',
  'como','cuando','donde','porque','cual','cuales','cuanto','muy',
  'mas','menos','ya','yo','tambien','tan','esta','estas','esto','asi',
  'es','son','fue','sea','ser','estar','hay','ha','han','he','has',
  'no','si','del','al'
]);

const normalize = (s) => (s || '')
  .toString()
  .toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // sin acentos
  .replace(/[^a-z0-9\s]/g, ' ')                    // solo letras/números/espacios
  .split(/\s+/)
  .filter(t => t && t.length > 1 && !STOPWORDS.has(t));

const tokensOf = (entry) => {
  // Los keywords pesan más (fueron elegidos a mano); el resto es texto.
  const kw = (entry.keywords || []).flatMap(normalize);
  const q  = normalize(entry.question);
  const a  = normalize(entry.answer);
  const t  = normalize(entry.tema);
  return { kw, q, a, t };
};

// Preindexar para evitar retokenizar en cada búsqueda.
const INDEX = KB.map(entry => ({
  entry,
  tokens: tokensOf(entry),
}));

/**
 * Busca en la KB. Retorna { hit, suggestions, score }.
 * Umbral mínimo para hit: 2 puntos (evita "matches" ridículos con 1 sola
 * coincidencia trivial). El caller decide qué hacer si hit=null.
 */
export function searchKB(rawQuery) {
  const qTokens = normalize(rawQuery);
  if (qTokens.length === 0) {
    return { hit: null, suggestions: [], score: 0 };
  }

  const scored = INDEX.map(({ entry, tokens }) => {
    let score = 0;
    for (const qt of qTokens) {
      // Match exacto en keywords vale 4.
      if (tokens.kw.includes(qt)) score += 4;
      // Match como sustring en algún keyword vale 2 (ej: "publicar" en "publicar-edicion").
      else if (tokens.kw.some(k => k.includes(qt) || qt.includes(k))) score += 2;
      // Match en tema vale 3.
      if (tokens.t.includes(qt)) score += 3;
      // Match en question vale 2.
      if (tokens.q.includes(qt)) score += 2;
      // Match en answer vale 1.
      if (tokens.a.includes(qt)) score += 1;
    }
    return { entry, score };
  }).filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0 || scored[0].score < 2) {
    return { hit: null, suggestions: [], score: 0 };
  }

  const hit = scored[0].entry;

  // Sugerencias: usa el campo related del hit si existe; si no, coge
  // los siguientes en el ranking.
  const relatedIds = hit.related || [];
  const relatedFromField = KB.filter(e => relatedIds.includes(e.id));

  const nextInRanking = scored.slice(1)
    .map(s => s.entry)
    .filter(e => !relatedIds.includes(e.id));

  const suggestions = [...relatedFromField, ...nextInRanking].slice(0, 3);

  return { hit, suggestions, score: scored[0].score };
}

/**
 * Devuelve los N temas más consultables (para pintar botones sugeridos
 * cuando el usuario abre el chat sin haber escrito nada).
 */
export function topSuggestedTopics(n = 6) {
  // Selección curada de temas típicos de arranque.
  const preferredIds = [
    'crear-articulo',
    'crear-edicion',
    'roles-general',
    'asignar-rol',
    'ediciones-nacional-vs-regional',
    'sin-acceso',
    'requiere-aprobacion',
    'home-filtrada',
  ];
  return preferredIds
    .map(id => KB.find(e => e.id === id))
    .filter(Boolean)
    .slice(0, n);
}
